import {
  SCREENER_BUILDER_WRAPPER_LOADING_PROPS,
  ScreenerBuilderWrapper,
} from "@/components/screener-builder/common";
import { capitalizeFirstLetter, getJobNameWithDisplayName } from "@/utils/ui";
import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { GenerationFailed } from "@/components/screener-builder/review/generation-failed";
import {
  useReviewPageQuery,
  useScreenerBuilderQuery,
  useSlimScreenerDetailsQuery,
} from "@/hooks/queries/screener-builder.queries";
import { useScreenerLocationByScreenerIdQuery } from "@/hooks/queries/screener-locations.queries";
import { useGetScreenerBuilderApiErrorHandler } from "@/hooks/screener-builder-hooks/screener-builder-common.hooks";
import {
  GenerationStatus,
  type ScreenerBuilderResponse,
  ScreenerBuilderStep,
  type SlimScreenerBuilderDetails,
} from "@/types/screener-builder";
import { DefaultUseQueryConfigParam } from "@/types/third-party";
import { AsyncOperationStatus, Maybe, type Nullable } from "@/types/utils";
import {
  generateAlternativeBuilderInfo,
  parseCompetencyDefinition,
} from "@/utils/alternative-screener-builder-data";
import {
  EMPTY_BUILDER_CONTEXT,
  isLegacyScreenerType,
  onLegacyAllowedStep,
  screenerIdIsNew,
} from "@/utils/screener-builder";

export const BUILDER_STATUS_NEW = "NEW";

interface UseBuilderInfoFromPilotQueryBaseReturn {
  builderInfo?: ScreenerBuilderResponse;
  errors: {
    builderQueryError: Nullable<Error>;
    pilotQueryError: Nullable<Error>;
  };
  status: AsyncOperationStatus | typeof BUILDER_STATUS_NEW;
}

interface UseBuilderInfoFromPilotQuerySuccess extends UseBuilderInfoFromPilotQueryBaseReturn {
  builderInfo: ScreenerBuilderResponse;
  errors: {
    builderQueryError: null;
    pilotQueryError: null;
  };
  status: AsyncOperationStatus.Success;
}

interface UseBuilderInfoFromPilotQueryError extends UseBuilderInfoFromPilotQueryBaseReturn {
  builderInfo: undefined;
  status: AsyncOperationStatus.Error;
}

interface UseBuilderInfoFromPilotQueryPending extends UseBuilderInfoFromPilotQueryBaseReturn {
  builderInfo: undefined;
  errors: {
    builderQueryError: null;
    pilotQueryError: null;
  };
  status: AsyncOperationStatus.Pending;
}

interface UseBuilderInfoFromPilotQueryNew extends UseBuilderInfoFromPilotQueryBaseReturn {
  builderInfo: undefined;
  errors: {
    builderQueryError: null;
    pilotQueryError: null;
  };
  status: typeof BUILDER_STATUS_NEW;
}

type UseBuilderInfoFromPilotQueryReturn =
  | UseBuilderInfoFromPilotQuerySuccess
  | UseBuilderInfoFromPilotQueryError
  | UseBuilderInfoFromPilotQueryPending
  | UseBuilderInfoFromPilotQueryNew;

// This hook first fires a pilot query to the "slim" screener details route to
// determine whether we are working with a legacy/CSV screener or an AI builder
// built screener. Based on the results of the pilot query it then either
// fetches or generates the builderInfo object for the given screener ID
export const useBuilderInfoFromPilotQuery = (
  screenerId: string,
  pilotQueryConfig: DefaultUseQueryConfigParam<SlimScreenerBuilderDetails> = {
    enabled: true,
  },
  builderInfoConfig: DefaultUseQueryConfigParam<ScreenerBuilderResponse> = {
    enabled: true,
  },
): UseBuilderInfoFromPilotQueryReturn => {
  const baseReturn = {
    builderInfo: undefined,
    errors: {
      builderQueryError: null,
      pilotQueryError: null,
    },
  };
  const idIsNew = screenerIdIsNew(screenerId);

  const { data: screenerDetails, error: slimQueryError } =
    useSlimScreenerDetailsQuery(screenerId, {
      ...pilotQueryConfig,
      enabled: !idIsNew && (pilotQueryConfig.enabled ?? true),
    });

  const builderQuery = useScreenerBuilderQuery(screenerId, {
    ...builderInfoConfig,
    enabled: !!(
      !idIsNew &&
      screenerDetails &&
      !isLegacyScreenerType(screenerDetails.type) &&
      (builderInfoConfig.enabled ?? true)
    ),
  });

  // Built and memoized rather than generated on demand to ensure that any
  // returned `builderInfo` object is a stable reference (the value returned
  // from TanStack query is stable already). This prevents consumers from
  // needing to guard against rerenders on an individual basis
  const alternativeBuilderInfo = useMemo(
    () =>
      screenerDetails && isLegacyScreenerType(screenerDetails.type)
        ? generateAlternativeBuilderInfo(
            screenerId,
            screenerDetails.jobTitle,
            screenerDetails.internalName,
          )
        : null,
    [screenerDetails, screenerId],
  );

  /////////////////////////
  //// Success Returns ////
  /////////////////////////
  if (idIsNew) {
    return {
      ...baseReturn,
      status: BUILDER_STATUS_NEW,
    };
  }

  // alternativeBuilderInfo is `null` unless we are on a legacy screener version
  if (alternativeBuilderInfo) {
    return {
      ...baseReturn,
      builderInfo: alternativeBuilderInfo,
      status: AsyncOperationStatus.Success,
    };
  }

  if (builderQuery.data) {
    return {
      ...baseReturn,
      builderInfo: builderQuery.data,
      status: AsyncOperationStatus.Success,
    };
  }

  //////////////////////
  //// Error Return ////
  //////////////////////
  if (slimQueryError || builderQuery.error) {
    return {
      ...baseReturn,
      errors: {
        builderQueryError: builderQuery.error,
        pilotQueryError: slimQueryError,
      },
      status: AsyncOperationStatus.Error,
    };
  }

  ////////////////////////
  //// Pending Return ////
  ////////////////////////
  return {
    ...baseReturn,
    status: AsyncOperationStatus.Pending,
  };
};

/**
 * Hook that handles automatic redirects based on builder info status and current step.
 * Redirects users to appropriate builder steps based on screener state.
 *
 * @param builderInfo - Result from pilot query hook
 * @param step - Current builder step from URL path, nullable when on
 *   /screener-builder/id with no step specified
 * @returns boolean indicating whether the redirect checks have completed
 */
export const useBuilderRedirect = (
  { builderInfo, status }: UseBuilderInfoFromPilotQueryReturn,
  step: Nullable<ScreenerBuilderStep>,
): boolean => {
  const router = useRouter();

  useEffect(() => {
    // Screener ID is "new" and we are not on Position
    if (
      status === BUILDER_STATUS_NEW &&
      step !== ScreenerBuilderStep.Position
    ) {
      return router.push("/screener-builder/new/position");
    }

    // The screener is legacy and we are not on Locations or Review
    if (
      builderInfo?.isLegacyScreener &&
      (step === null || !onLegacyAllowedStep(step))
    ) {
      return router.replace(
        `/screener-builder/${builderInfo.screenerId}/locations`,
      );
    }

    // On the root /screener-builder/<ID> route and need to determine step
    if (step === null && builderInfo && !builderInfo.isLegacyScreener) {
      return router.replace(
        `/screener-builder/${builderInfo.screenerId}/${builderInfo.step}`,
      );
    }
  }, [builderInfo, router, status, step]);

  // Return value used to inform consumers whether redirect checks have
  // completed. This is done to avoid a race condition in which a legacy
  // screener would incorrectly render on a disallowed page for a flash before
  // this hook's redirect could trigger.

  // If we're on a non-legacy-allowed step and don't know if it's legacy yet,
  // block rendering
  if (step && !onLegacyAllowedStep(step) && !builderInfo) {
    return false;
  }

  // If we know it's a legacy screener and we're on a non-allowed step, block
  // rendering
  if (builderInfo?.isLegacyScreener && step && !onLegacyAllowedStep(step)) {
    return false;
  }

  // Safe to render in all other cases
  return true;
};

const useBuilderInfoAndPageErrorHandler = (
  builderInfo: UseBuilderInfoFromPilotQueryReturn,
  pageError: Nullable<Error>,
  customHandler?: (err: Error) => void,
) => {
  const defaultErrorHandler = useGetScreenerBuilderApiErrorHandler();
  const errorHandler = useMemo(
    () => customHandler ?? defaultErrorHandler,
    [customHandler, defaultErrorHandler],
  );

  useEffect(() => {
    if (builderInfo.status === AsyncOperationStatus.Error) {
      return errorHandler(
        // Safe to cast as the status is only set to Error if at least one of
        // these errors exists.
        // Preference is given to the pilot query error as, if it exists, the
        // builderInfo query will not have fired
        (builderInfo.errors.pilotQueryError ??
          builderInfo.errors.builderQueryError) as Error,
      );
    }

    if (pageError) {
      return errorHandler(pageError);
    }
  }, [builderInfo, errorHandler, pageError]);
};

// Backstop for generation that never resolves (worker crashed, message never
// picked up, etc.). Without it the review page polls forever and shows an
// infinite spinner — the "questions page gets stuck" symptom. When generation
// is hung this long we surface the existing GenerationFailed state instead.
// Set well above the worker's own worst-case so we never false-error a
// slow-but-working generation: the screener_generator retries up to 3 times,
// each attempt costing ~30s builder fetch + ~90s LLM + ~30s status write
// (~7.5 min total), plus requeue cycles and SQS redelivery on top. 15 minutes
// clears that with margin while still bounding the spinner.
const GENERATION_POLL_TIMEOUT_MS = 15 * 60 * 1000;

const REVIEW_POLL_INTERVAL_MS = 2500;

// Pure helpers shared between refetchInterval and the hook body. refetchInterval
// MUST NOT close over the derived `generationTimedOut`/`generationSession` consts
// declared lower in useReviewPageData: React Query evaluates refetchInterval
// synchronously while constructing the QueryObserver (before those consts
// initialize), which threw a temporal-dead-zone "Cannot access before
// initialization" ReferenceError and crashed the whole review page. These helpers
// only depend on values available before the query call (status + screenerId).
const isGeneratingStatus = (status?: GenerationStatus): boolean =>
  status === undefined ||
  status === GenerationStatus.Generating ||
  status === GenerationStatus.New;

const generationSessionKey = (
  screenerId: string,
  isGenerating: boolean,
): string => `${screenerId}:${isGenerating}`;

// Minimal Error-state builderInfo used when the backstop fires before the
// builder query ever resolved (no builderInfo to spread). Lets the review page
// reach GenerationFailed instead of hanging on the loading wrapper — the
// GenerationFailed UI doesn't render position/context details, so empty values
// are fine.
const buildTimedOutBuilderInfo = (
  screenerId: string,
): ScreenerBuilderResponse => ({
  data: {
    context: EMPTY_BUILDER_CONTEXT,
    position: {
      internalName: null,
      isHighTurnoverRole: null,
      jobDescription: "",
      jobTitle: "",
      seniorityLevel: null,
    },
  },
  generationStatus: GenerationStatus.Error,
  isLegacyScreener: false,
  published: false,
  screenerId,
  step: ScreenerBuilderStep.Review,
});

export const useReviewPageData = (screenerId: string) => {
  // Stores which generation "session" (screenerId + generating state) the
  // backstop timer fired for. `generationTimedOut` is derived by comparing it
  // against the current session below, rather than reset via setState in the
  // effect — a leaked timeout from a previous screener or run simply won't
  // match the current session, so it can't surface GenerationFailed for a
  // fresh one. The review page component is reused across screenerId changes
  // (App Router).
  const [timedOutSession, setTimedOutSession] =
    useState<Nullable<string>>(null);

  const builderInfoResponse = useBuilderInfoFromPilotQuery(
    screenerId,
    undefined,
    {
      refetchInterval: ({ state: { data, error } }) => {
        // If we are in an incomplete, non-error generation state we regularly
        // poll the API until generation is finished (or the backstop fires).
        // Compute timed-out from `timedOutSession` (declared above) — never the
        // `generationTimedOut` const below, which isn't initialized yet when
        // React Query first evaluates this during observer setup (TDZ crash).
        const generating = !data || isGeneratingStatus(data.generationStatus);
        const timedOut =
          timedOutSession === generationSessionKey(screenerId, generating);
        if (generating && !error && !timedOut) {
          return REVIEW_POLL_INTERVAL_MS;
        }

        return false;
      },
    },
  );

  const generationStatus = builderInfoResponse.builderInfo?.generationStatus;
  const isGenerating = isGeneratingStatus(generationStatus);

  const generationSession = generationSessionKey(screenerId, isGenerating);
  const generationTimedOut = timedOutSession === generationSession;

  useEffect(() => {
    if (!isGenerating) {
      return;
    }

    const timer = setTimeout(
      () => setTimedOutSession(generationSession),
      GENERATION_POLL_TIMEOUT_MS,
    );

    return () => clearTimeout(timer);
  }, [isGenerating, generationSession]);

  // Coerce a hung generation into the Error state so the existing
  // GenerationFailed UI renders rather than spinning indefinitely. The builder
  // query may still be pending (no builderInfo) when the backstop fires — fall
  // back to a synthetic Error response so the page still reaches
  // GenerationFailed instead of hanging on the loading wrapper.
  const builderInfo = useMemo(() => {
    if (!generationTimedOut || !isGenerating) {
      return builderInfoResponse.builderInfo;
    }

    return {
      ...(builderInfoResponse.builderInfo ??
        buildTimedOutBuilderInfo(screenerId)),
      generationStatus: GenerationStatus.Error,
    };
  }, [
    generationTimedOut,
    isGenerating,
    builderInfoResponse.builderInfo,
    screenerId,
  ]);
  const review = useReviewPageQuery(
    screenerId,
    {
      enabled:
        builderInfoResponse.builderInfo?.generationStatus ===
        GenerationStatus.Complete,
    },
    // CSV competencies are returned from the API with scoringGuidelines set to
    // empty strings and the actual scoring guidelines living in a single string
    // in the definition. We parse this out in a select here, rather than on the
    // query itself, because the query in isolation is unable to definitively
    // determine whether it is a CSV screener
    ({ competencies, ...rest }) => ({
      competencies: builderInfoResponse.builderInfo?.isLegacyScreener
        ? competencies.map(({ definition, ...competency }) => ({
            ...competency,
            definition,
            scoringGuidelines: parseCompetencyDefinition(definition),
          }))
        : competencies,
      ...rest,
    }),
  );

  useBuilderInfoAndPageErrorHandler(builderInfoResponse, review.error);

  useBuilderRedirect(builderInfoResponse, ScreenerBuilderStep.Review);

  return {
    builderInfoResponse: { ...builderInfoResponse, builderInfo },
    review,
  };
};

export const useLocationPageData = (screenerId: string) => {
  const builderInfoResponse = useBuilderInfoFromPilotQuery(screenerId);
  const locations = useScreenerLocationByScreenerIdQuery(screenerId, {
    enabled: !!builderInfoResponse.builderInfo,
  });

  useBuilderInfoAndPageErrorHandler(builderInfoResponse, locations.error);

  useBuilderRedirect(builderInfoResponse, ScreenerBuilderStep.Locations);

  return {
    builderInfoResponse,
    locations,
  };
};

/**
 * Hook that returns appropriate UI based on loading states of builder info and page-specific query data.
 * Handles loading states and renders the final UI when both data sources are ready.
 *
 * @param builderInfo - Builder info from pilot query, undefined while loading
 * @param pageQueryData - Page-specific query data, undefined while loading
 * @param step - Current builder step for UI configuration
 * @param renderLoadingFinished - Function to render final UI when all data is loaded
 * @param redirectChecksComplete - Whether redirect checks have completed and it's safe to render content (defaults to true)
 * @returns ReactNode appropriate for current loading state
 */
export const useLoadingUIByPilotQueryResponse = <T,>(
  builderInfo: Maybe<ScreenerBuilderResponse>,
  pageQueryData: Maybe<T>,
  step: ScreenerBuilderStep,
  renderLoadingFinished: (
    builderInfo: ScreenerBuilderResponse,
    pageQueryData: T,
  ) => ReactNode,
  redirectChecksComplete = true,
) => {
  const pageTitle = useMemo(() => capitalizeFirstLetter(step), [step]);

  return useMemo(() => {
    if (!builderInfo) {
      return (
        <ScreenerBuilderWrapper
          {...SCREENER_BUILDER_WRAPPER_LOADING_PROPS}
          // When on a legacy allowed step, default to the legacy navigation to
          // reduce UI jitter that occurs as component data and feature flags
          // are initialized
          isLegacyScreener={onLegacyAllowedStep(step)}
          pageTitle={pageTitle}
        />
      );
    }

    const sharedProps = {
      ...SCREENER_BUILDER_WRAPPER_LOADING_PROPS,
      generationStatus: builderInfo.generationStatus,
      isLegacyScreener: builderInfo.isLegacyScreener,
      jobDisplayName: getJobNameWithDisplayName(
        builderInfo.data.position.jobTitle,
        builderInfo.data.position.internalName,
      ),
      pageTitle,
      published: builderInfo.published,
    };

    if (!redirectChecksComplete) {
      return <ScreenerBuilderWrapper {...sharedProps} />;
    }

    if (
      // Review is the only step that relies on generation success
      step === ScreenerBuilderStep.Review &&
      builderInfo.generationStatus === GenerationStatus.Error
    ) {
      return <GenerationFailed wrapperProps={sharedProps} />;
    }

    if (!pageQueryData) {
      return <ScreenerBuilderWrapper {...sharedProps} />;
    }

    return renderLoadingFinished(builderInfo, pageQueryData);
  }, [
    builderInfo,
    pageQueryData,
    pageTitle,
    renderLoadingFinished,
    step,
    redirectChecksComplete,
  ]);
};
